import {
  AnchoredPopup,
  GpuCardIcon,
  IconButton,
  RotateCcwIcon,
  Spinner,
  WarningIcon,
  useAnchoredPopupAnchor,
} from "@carbon6/c6-frontend-design";
import {
  useCallback,
  useSyncExternalStore,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { isCapacityIncomplete } from "@/api";
import { HatTextTooltip } from "@/components/tooltip";
import { GpuCapacityPanel } from "./GpuCapacityPanel";
import { gpuCapacityController } from "./gpuCapacityController";
import { gpuFreeTone, gpuWidgetLabel, gpuWidgetTooltip } from "./gpuCapacityFormat";
import { GpuFreeToneDot } from "./GpuFreeToneDot";
import styles from "./GpuCapacityWidget.module.css";

type GpuCapacityWidgetProps = {
  // 펼친 상태에서는 여유 톤 점 + 실시간 라벨 행으로 렌더한다. 접힘 레일에서는 GPU 아이콘 트리거로 렌더한다.
  collapsed: boolean;
};

// GPU 가용량은 모든 화면에서 항상 보여야 한다. 그래서 사이드바 하단(MCP 연결 위)에 항상 둔다.
// 트리거에는 판단에 필요한 최소 값(여유/전체 · 대기 job)만 둔다. 나머지는 클릭 팝업이 표시한다.
// 데이터는 모듈 싱글턴 컨트롤러가 소유한다. 그래서 페이지를 옮겨도 폴링과 직전 값이 유지된다.
export function GpuCapacityWidget({ collapsed }: GpuCapacityWidgetProps): ReactNode {
  const popup = useAnchoredPopupAnchor();
  const snapshot = useSyncExternalStore(
    gpuCapacityController.subscribe,
    gpuCapacityController.getSnapshot,
    gpuCapacityController.getSnapshot,
  );

  const handleTriggerClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      if (popup.open) {
        popup.close();
        return;
      }
      popup.openFromElement(event);
    },
    [popup],
  );

  // 사람이 누른 새로고침에만 fresh 를 추가해 upstream 캐시를 건너뛴다.
  const handleRefreshClick = useCallback(() => {
    void gpuCapacityController.refresh({ fresh: true });
  }, []);

  const { data, loading, failed } = snapshot;
  const triggerClassName = collapsed ? styles.iconTrigger : styles.row;

  if (data === null) {
    // 첫 로드 전에는 트리거 위치에 스피너를 표시한다. 그래서 하단 슬롯 레이아웃이 바뀌지 않는다.
    // 첫 조회부터 실패했으면 위젯을 숨기지 않고 값을 모른다고 표시한다. 사라진 위젯은 사용자가 알아채기 어렵다.
    return loading ? (
      <button type="button" className={triggerClassName} disabled>
        <Spinner size="sm" label="GPU 가용량을 불러오는 중입니다." />
      </button>
    ) : (
      <HatTextTooltip
        text="GPU 가용량을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요."
        placement="right"
        mode="plain"
      >
        <button
          type="button"
          className={triggerClassName}
          data-failed="true"
          aria-label={collapsed ? "GPU —" : undefined}
          onClick={handleRefreshClick}
        >
          <WarningIcon className={styles.warningIcon} aria-hidden="true" />
          {collapsed ? null : <span className={styles.label}>GPU —</span>}
        </button>
      </HatTextTooltip>
    );
  }

  // 조회 실패(failed)와 수치 불완전은 다른 축이다. 표시는 하나로 합치고, 판정은 각 소유자가 한다.
  const degraded = failed || isCapacityIncomplete(data);
  const tone = gpuFreeTone(data);

  return (
    <>
      <HatTextTooltip
        text={gpuWidgetTooltip(data)}
        placement="right"
        mode="plain"
        disabled={popup.open}
      >
        <button
          type="button"
          className={triggerClassName}
          aria-label={collapsed ? gpuWidgetLabel(data) : undefined}
          aria-haspopup="dialog"
          aria-expanded={popup.open}
          data-open={popup.open ? "true" : "false"}
          data-stale={failed ? "true" : "false"}
          onClick={handleTriggerClick}
        >
          {collapsed ? (
            <>
              <GpuCardIcon className={styles.icon} />
              {/* 접힘 레일에는 숫자를 표시할 수 없다. 그래서 여유 톤 점을 아이콘 우상단에 겹쳐 톤만 표시한다. */}
              <span className={styles.iconToneDot}>
                <GpuFreeToneDot tone={tone} />
              </span>
            </>
          ) : (
            <>
              <GpuFreeToneDot tone={tone} />
              <span className={styles.label}>
                {gpuWidgetLabel(data)}
                {degraded ? (
                  <WarningIcon className={styles.warningIcon} aria-hidden="true" />
                ) : null}
              </span>
            </>
          )}
        </button>
      </HatTextTooltip>
      <AnchoredPopup
        open={popup.open}
        anchor={popup.anchor}
        onClose={popup.close}
        placement="right-end"
        role="dialog"
        aria-label="GPU 가용량"
        width="20rem"
        // 팝업 배경을 사이드바와 같은 surface 톤으로 맞춘다. c6 Panel 기본값은 surfaceRaised 다.
        // 그래서 style 로 덮어써 styled-component 기본값보다 우선하게 한다.
        style={{ backgroundColor: "var(--c6-color-bg-surface)" }}
      >
        <AnchoredPopup.Header divider className={styles.header}>
          <span className={styles.headerTitle}>GPU 가용량</span>
          <IconButton
            aria-label="새로고침"
            title="최신 GPU 가용량을 다시 받아옵니다."
            size="sm"
            variant="surface"
            onClick={handleRefreshClick}
          >
            <RotateCcwIcon />
          </IconButton>
        </AnchoredPopup.Header>
        <AnchoredPopup.Body padding="md">
          <GpuCapacityPanel capacity={data} stale={failed} />
        </AnchoredPopup.Body>
      </AnchoredPopup>
    </>
  );
}
